import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Plus, Trash2, Pencil } from 'lucide-react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { gql } from '~/__generated__'
import { UserRole, UserStatus } from '~/__generated__/graphql'
import { TablePageLayout } from '~/layouts/table-page-layout'
import { Button } from '~/ui/button'
import { GraphqlError } from '~/ui/errors'
import { toast } from '~/hooks/use-toast'
import { useSafeMutation } from '~/common/use-safe-mutation'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/ui/dialog'
import { Form } from '~/ui/form'
import { TextField } from '~/fields/text-field'
import { SelectField } from '~/fields/select-field'
import { DataTable } from '~/admin/data-table'
import { ColumnDef } from '@tanstack/react-table'

const USERS_QUERY = gql(`
  query UsersAdmin($first: Int = 50, $after: String) {
    users(first: $first, after: $after) {
      edges {
        node {
          id
          firstName
          lastName
          fullName
          email
          userRole
          userStatus
          timeZone
          createdAt
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

const CREATE_USER_MUTATION = gql(`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      user {
        id
        firstName
        lastName
        fullName
        email
        userRole
        userStatus
        timeZone
        createdAt
      }
      errors
    }
  }
`)

const UPDATE_USER_MUTATION = gql(`
  mutation UserUpdateAdmin($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      user {
        id
        firstName
        lastName
        fullName
        email
        userRole
        userStatus
        timeZone
        createdAt
      }
    }
  }
`)

const DELETE_USER_MUTATION = gql(`
  mutation DeleteUser($input: DeleteUserInput!) {
    deleteUser(input: $input) {
      success
      errors
    }
  }
`)

const userFormSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  userRole: z.enum(['SYSTEM_ADMIN', 'ORG_ADMIN', 'MEMBER']).default('MEMBER'),
  userStatus: z.enum(['INVITED', 'ACTIVE', 'BLOCKED']).default('INVITED'),
  timeZone: z.string().min(1),
})

type UserFormValues = z.infer<typeof userFormSchema>

const toApiUserRole = (role: UserFormValues['userRole']): UserRole => {
  switch (role) {
    case 'SYSTEM_ADMIN':
      return UserRole.SystemAdmin
    case 'ORG_ADMIN':
      return UserRole.OrgAdmin
    case 'MEMBER':
    default:
      return UserRole.Member
  }
}

const toApiUserStatus = (status: UserFormValues['userStatus']): UserStatus => {
  switch (status) {
    case 'ACTIVE':
      return UserStatus.Active
    case 'BLOCKED':
      return UserStatus.Blocked
    case 'INVITED':
    default:
      return UserStatus.Invited
  }
}

export const UsersScreen = () => {
  const { data, error, fetchMore, refetch } = useQuery(USERS_QUERY)
  const [createUser] = useSafeMutation(CREATE_USER_MUTATION)
  const [userUpdate] = useSafeMutation(UPDATE_USER_MUTATION)
  const [deleteUser] = useSafeMutation(DELETE_USER_MUTATION)

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<any | null>(null)

  const users = data?.users.edges.map((e: any) => e.node) ?? []
  const pageInfo = data?.users.pageInfo

  const createForm = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      userRole: 'MEMBER',
      userStatus: 'INVITED',
      timeZone: 'UTC',
    },
  })

  const editForm = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      userRole: 'MEMBER',
      userStatus: 'ACTIVE',
      timeZone: 'UTC',
    },
  })

  const columns = useMemo<ColumnDef<any>[]>(() => {
    return [
      {
        accessorKey: 'fullName',
        header: 'Name',
        cell: ({ row }) => (
          <div className="text-sm">
            <div>{row.original?.fullName}</div>
            <div className="text-muted-foreground">{row.original?.email}</div>
          </div>
        ),
      },
      {
        accessorKey: 'userRole',
        header: 'Role',
      },
      {
        accessorKey: 'userStatus',
        header: 'Status',
      },
      {
        accessorKey: 'timeZone',
        header: 'Time Zone',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const u = row.original
          return (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="xs"
                className="cursor-pointer"
                onClick={() => {
                  setEditingUser(u)
                  editForm.reset({
                    firstName: u.firstName,
                    lastName: u.lastName,
                    email: u.email,
                    userRole: u.userRole,
                    userStatus: u.userStatus,
                    timeZone: u.timeZone,
                  })
                  setEditOpen(true)
                }}
              >
                <Pencil className="mr-1 h-3 w-3" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="xs"
                className="cursor-pointer text-red-600"
                onClick={async () => {
                  if (!confirm(`Delete user "${u.fullName}"? This cannot be undone.`)) return
                  const res = await deleteUser({ variables: { input: { id: u.id } } })
                  if (res.data?.deleteUser?.success) {
                    toast({ title: 'User deleted' })
                    await refetch()
                  } else {
                    toast({
                      title: 'Delete failed',
                      description: (res.data?.deleteUser?.errors || []).join(', ') || 'Failed to delete user',
                      variant: 'destructive',
                    })
                  }
                }}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Delete
              </Button>
            </div>
          )
        },
      },
    ]
  }, [deleteUser, editForm, refetch])

  if (error) return <GraphqlError error={error} />

  return (
    <TablePageLayout padding={false}>
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-7 py-3">
        <h1 className="text-2xl">Users</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="cursor-pointer">
              <Plus className="mr-2 h-3.5 w-3.5" />
              <span className="text-sm">New User</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
            </DialogHeader>
            <Form {...createForm}>
              <form
                className="flex flex-col gap-4"
                onSubmit={createForm.handleSubmit(async (values) => {
                  const res = await createUser({
                    variables: {
                      input: {
                        userInput: {
                          firstName: values.firstName,
                          lastName: values.lastName,
                          email: values.email,
                          userRole: toApiUserRole(values.userRole),
                          userStatus: toApiUserStatus(values.userStatus),
                          timeZone: values.timeZone,
                        },
                      },
                    },
                  })

                  if (res.data?.createUser?.errors?.length) {
                    toast({
                      title: 'Create failed',
                      description: res.data.createUser.errors.join(', '),
                      variant: 'destructive',
                    })
                    return
                  }

                  toast({
                    title: 'User created',
                    description: 'Reset password instructions were sent to the user.',
                  })
                  setCreateOpen(false)
                  createForm.reset()
                  await refetch()
                })}
              >
                <div className="grid grid-cols-2 gap-3">
                  <TextField control={createForm.control} name="firstName" label="First name" required />
                  <TextField control={createForm.control} name="lastName" label="Last name" required />
                </div>
                <TextField control={createForm.control} name="email" label="Email" required />
                <div className="grid grid-cols-2 gap-3">
                  <SelectField
                    control={createForm.control}
                    name="userRole"
                    label="Role"
                    placeholder="Select role"
                    selectEntries={[
                      { value: 'MEMBER', label: 'Member' },
                      { value: 'ORG_ADMIN', label: 'Org Admin' },
                      { value: 'SYSTEM_ADMIN', label: 'System Admin' },
                    ]}
                    required
                  />
                  <SelectField
                    control={createForm.control}
                    name="userStatus"
                    label="Status"
                    placeholder="Select status"
                    selectEntries={[
                      { value: 'INVITED', label: 'Invited' },
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'BLOCKED', label: 'Blocked' },
                    ]}
                    required
                  />
                </div>
                <TextField control={createForm.control} name="timeZone" label="Time zone" required />
                <DialogFooter>
                  <Button type="button" variant="link" onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="cursor-pointer">
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-7">
        <DataTable columns={columns} data={users} />
        {pageInfo?.hasNextPage && (
          <div className="mt-4">
            <Button
              variant="outline"
              className="disabled:bg-gray-100"
              onClick={() => fetchMore({ variables: { after: pageInfo.endCursor } })}
              disabled={!pageInfo.hasNextPage}
            >
              Load more
            </Button>
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form
              className="flex flex-col gap-4"
              onSubmit={editForm.handleSubmit(async (values) => {
                if (!editingUser) return
                await userUpdate({
                  variables: {
                    input: {
                      id: editingUser.id,
                      userInput: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        userRole: toApiUserRole(values.userRole),
                        userStatus: toApiUserStatus(values.userStatus),
                        timeZone: values.timeZone,
                      },
                    },
                  },
                })
                toast({ title: 'User updated' })
                setEditOpen(false)
                setEditingUser(null)
                await refetch()
              })}
            >
              <div className="grid grid-cols-2 gap-3">
                <TextField control={editForm.control} name="firstName" label="First name" required />
                <TextField control={editForm.control} name="lastName" label="Last name" required />
              </div>
              <TextField control={editForm.control} name="email" label="Email" required />
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  control={editForm.control}
                  name="userRole"
                  label="Role"
                  placeholder="Select role"
                  selectEntries={[
                    { value: 'MEMBER', label: 'Member' },
                    { value: 'ORG_ADMIN', label: 'Org Admin' },
                    { value: 'SYSTEM_ADMIN', label: 'System Admin' },
                  ]}
                  required
                />
                <SelectField
                  control={editForm.control}
                  name="userStatus"
                  label="Status"
                  placeholder="Select status"
                  selectEntries={[
                    { value: 'INVITED', label: 'Invited' },
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'BLOCKED', label: 'Blocked' },
                  ]}
                  required
                />
              </div>
              <TextField control={editForm.control} name="timeZone" label="Time zone" required />
              <DialogFooter>
                <Button type="button" variant="link" onClick={() => setEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="cursor-pointer">
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </TablePageLayout>
  )
}
